// src/utils/htmlProjection.js

/**
 * Espelho em JavaScript de `HtmlProjection.ToPlainText` do backend
 * (`LitteraeApi/Html/HtmlProjection.cs`).
 *
 * Projeta HTML canônico na sua representação em texto puro — a régua onde os
 * offsets das notas (`textRangeStart` / `textRangeEnd`) são medidos:
 *
 *     projeção = concatenação dos text nodes + "\n" sintético nas fronteiras de bloco
 *
 * O `\n` existe para que uma seleção que atravessa dois parágrafos não gere
 * excerto com as palavras coladas ("...parágrafo.Segundo parágrafo..."). O custo é
 * que esse caractere não existe em text node nenhum do DOM, então o mapeamento
 * seleção→offset precisa saltá-lo — daí cada segmento marcar se é sintético.
 *
 * ⚠️ ESTE ARQUIVO É METADE DE UM PAR. A outra metade está em C#, e as duas
 * precisam produzir exatamente o mesmo texto para o mesmo HTML. Divergência entre
 * elas **não gera erro**: gera nota deslocada, e só aparece em produção. Qualquer
 * mudança aqui exige a mudança equivalente lá, e a suite cruzada rodando dos dois
 * lados.
 *
 * **Pré-condição:** o HTML já passou pelo `HtmlNormalizer` do backend. A projeção
 * não normaliza. Projetar HTML cru exigiria uma tabela de correspondência dentro de
 * cada text node, porque o colapso de espaço remove caracteres no meio do texto
 * ("O␣␣Velho" de 8 vira "O␣Velho" de 7, e o "V" sai da posição 3 para a 2).
 */

const TEXT_NODE = 3;
const ELEMENT_NODE = 1;

/**
 * Tags cujo **fecho** emite o `\n` sintético. `br` é tratado à parte: emite na
 * própria posição, não no fecho.
 *
 * Espelha `HtmlVocabulary.ProjectionBreakTags`.
 *
 * Note quem está de fora: `ul`, `ol`, `table`, `thead`, `tbody` e `tr`. Quem quebra
 * é o item, não o contêiner — a última célula da linha já emitiu a quebra dela, e
 * emitir no `tr` também geraria linha em branco entre linhas da tabela.
 *
 * `figure` segue a mesma lógica e fica de fora; `figcaption` entra. Figura sem
 * legenda não emite caractere nenhum — igual à imagem solta.
 */
export const PROJECTION_BREAK_TAGS = new Set([
  "p",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "blockquote",
  "li",
  "hr",
  "pre",
  "th",
  "td",
  "figcaption",
]);

/**
 * Todas as tags de bloco da whitelist, incluindo os contêineres.
 *
 * Espelha `HtmlVocabulary.BlockTags`. **Não é o conjunto usado pela projeção** —
 * existe para o renderizador e o CSS da Fase 5. No backend ele serve à regra de
 * colapso de espaço, que não tem equivalente aqui porque o HTML chega normalizado.
 *
 * Trocar um conjunto pelo outro em `toPlainText` inseriria `\n` a mais em toda
 * lista e toda tabela, deslocando silenciosamente as notas dali em diante.
 */
export const BLOCK_TAGS = new Set([
  ...PROJECTION_BREAK_TAGS,
  "ul",
  "ol",
  "table",
  "thead",
  "tbody",
  "tr",
  "figure",
]);

/**
 * Único ponto de construção de segmento, para que a invariante
 * `synthetic ⟺ node === null` não tenha como ser violada.
 *
 * @param {number} start Offset na projeção, inclusive.
 * @param {number} end Offset na projeção, exclusive.
 * @param {Text|null} node Text node de origem; null se e somente se sintético.
 */
function createSegment(start, end, node) {
  return { start, end, node, synthetic: node === null };
}

/**
 * Projeta HTML canônico em texto puro.
 *
 * @param {string|Element|null|undefined} source HTML já normalizado pelo backend,
 *   ou um elemento raiz já parseado (útil para o renderizador, que não quer pagar
 *   um segundo parse).
 * @returns {{ text: string, map: Array<{start: number, end: number, node: Text|null, synthetic: boolean}> }}
 *   `text` é a projeção. `map` são segmentos contíguos e ordenados cobrindo o texto
 *   inteiro: `map[i].end === map[i+1].start`, o primeiro começa em 0 e o último
 *   termina em `text.length`.
 *
 *   O mapa **nunca trafega e nunca é serializado**: cada lado o constrói a partir do
 *   mesmo HTML, com os nós do seu próprio DOM.
 */
export function toPlainText(source) {
  const root = resolveRoot(source);
  if (!root) return { text: "", map: [] };

  const state = { parts: [], map: [], length: 0 };
  walk(root, state);

  return { text: state.parts.join(""), map: state.map };
}

function resolveRoot(source) {
  if (source === null || source === undefined || source === "") return null;

  if (typeof source === "string") {
    return new DOMParser().parseFromString(source, "text/html").body;
  }

  return source;
}

function walk(parent, state) {
  for (const child of parent.childNodes) {
    if (child.nodeType === TEXT_NODE) {
      // Depois da normalização todo text node contribui; o teste de vazio é só
      // uma guarda contra HTML não normalizado.
      const data = child.data;
      if (data.length === 0) continue;

      state.map.push(createSegment(state.length, state.length + data.length, child));
      state.parts.push(data);
      state.length += data.length;
      continue;
    }

    // Comentário, CDATA e afins não contribuem com caractere nenhum.
    if (child.nodeType !== ELEMENT_NODE) continue;

    const name = child.localName;

    // <br> quebra na própria posição, não no fecho.
    if (name === "br") {
      appendSyntheticBreak(state);
      continue;
    }

    walk(child, state);

    // As quebras dentro de <pre> são caracteres reais do text node e já entraram
    // acima; só o fecho do <pre> emite sintético. <hr> é vazio: o walk não faz
    // nada e a quebra sai aqui.
    if (PROJECTION_BREAK_TAGS.has(name)) appendSyntheticBreak(state);
  }
}

function appendSyntheticBreak(state) {
  state.map.push(createSegment(state.length, state.length + 1, null));
  state.parts.push("\n");
  state.length += 1;
}
